'use client'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getAuthHeaders } from '@/lib/api-utils'
import { fetchWithAuthHandler } from '@/lib/api-error-handler'

// ============================================================================
// QUERY KEYS - Centralized for cache management
// ============================================================================

export const apiKeys = {
    currentUser: () => ['currentUser'] as const,
    projects: (limit?: number) => ['projects', { limit }] as const,
    project: (id: string) => ['project', id] as const,
    projectMembers: (id: string) => ['project', id, 'members'] as const,
    projectVersions: (id: string) => ['project', id, 'versions'] as const,
    trackers: () => ['trackers'] as const,
    issueStatuses: () => ['issueStatuses'] as const,
    priorities: () => ['priorities'] as const,
    timeActivities: () => ['timeActivities'] as const,
}

// ============================================================================
// CURRENT USER
// ============================================================================

interface CurrentUser {
    id: number
    name: string
    login: string
    firstname?: string
    lastname?: string
    mail?: string
    admin?: boolean
    api_key?: string
}

async function fetchCurrentUser(): Promise<CurrentUser> {
    const authHeaders = getAuthHeaders()
    const response = await fetchWithAuthHandler('/api/my', {
        headers: authHeaders,
    })

    if (!response.ok) {
        throw new Error(`Failed to fetch current user: ${response.status}`)
    }

    const data = await response.json()
    const user = data.user || data

    // Normalize the user data
    return {
        ...user,
        name: user.name || `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.login || 'Current User',
    }
}

/**
 * Hook to fetch and cache the current user
 * Uses React Query for automatic caching and deduplication
 */
export function useCurrentUser() {
    return useQuery({
        queryKey: apiKeys.currentUser(),
        queryFn: fetchCurrentUser,
        staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
        gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (formerly cacheTime)
        refetchOnWindowFocus: false, // Don't refetch when window regains focus
        retry: 2, // Retry failed requests twice
    })
}

// ============================================================================
// PROJECTS
// ============================================================================

interface Project {
    id: number
    name: string
    identifier: string
    description?: string
    status: number
    is_public?: boolean
    parent?: {
        id: number
        name: string
    }
    created_on?: string
    updated_on?: string
}

interface ProjectsResponse {
    projects: Project[]
    total_count: number
    offset: number
    limit: number
}

async function fetchProjects(limit: number = 1000): Promise<ProjectsResponse> {
    const authHeaders = getAuthHeaders()
    const response = await fetchWithAuthHandler(`/api/projects?limit=${limit}`, {
        headers: authHeaders,
    })

    if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.status}`)
    }

    return response.json()
}

/**
 * Hook to fetch and cache all projects
 * Default limit: 1000 projects
 */
export function useProjects(limit: number = 1000) {
    return useQuery({
        queryKey: apiKeys.projects(limit),
        queryFn: () => fetchProjects(limit),
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        refetchOnWindowFocus: false,
    })
}

// ============================================================================
// PROJECT MEMBERS
// ============================================================================

interface ProjectMember {
    id: number
    project: {
        id: number
        name: string
    }
    user: {
        id: number
        name: string
    }
    roles: Array<{
        id: number
        name: string
    }>
}

async function fetchProjectMembers(projectId: string): Promise<ProjectMember[]> {
    const authHeaders = getAuthHeaders()
    const response = await fetchWithAuthHandler(`/api/projects/${projectId}/members`, {
        headers: authHeaders,
    })

    if (!response.ok) {
        throw new Error(`Failed to fetch project members: ${response.status}`)
    }

    const data = await response.json()
    return data.memberships || []
}

/**
 * Hook to fetch and cache project members
 */
export function useProjectMembers(projectId: string | undefined | null) {
    return useQuery({
        queryKey: projectId ? apiKeys.projectMembers(projectId) : ['projectMembers', 'none'],
        queryFn: () => {
            if (!projectId) throw new Error('Project ID is required')
            return fetchProjectMembers(projectId)
        },
        enabled: !!projectId && projectId !== 'all', // Only fetch if projectId is valid
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        refetchOnWindowFocus: false,
    })
}

// ============================================================================
// PROJECT VERSIONS
// ============================================================================

interface Version {
    id: number
    name: string
    description?: string
    status: string
    due_date?: string
    created_on: string
    updated_on: string
}

async function fetchProjectVersions(projectId: string): Promise<Version[]> {
    const authHeaders = getAuthHeaders()
    const response = await fetchWithAuthHandler(`/api/projects/${projectId}/versions`, {
        headers: authHeaders,
    })

    if (!response.ok) {
        throw new Error(`Failed to fetch project versions: ${response.status}`)
    }

    const data = await response.json()
    return data.versions || []
}

/**
 * Hook to fetch and cache project versions
 */
export function useProjectVersions(projectId: string | undefined | null) {
    return useQuery({
        queryKey: projectId ? apiKeys.projectVersions(projectId) : ['projectVersions', 'none'],
        queryFn: () => {
            if (!projectId) throw new Error('Project ID is required')
            return fetchProjectVersions(projectId)
        },
        enabled: !!projectId && projectId !== 'all',
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    })
}

// ============================================================================
// TRACKERS
// ============================================================================

interface Tracker {
    id: number
    name: string
    default_status?: {
        id: number
        name: string
    }
}

async function fetchTrackers(): Promise<Tracker[]> {
    const authHeaders = getAuthHeaders()
    const response = await fetchWithAuthHandler('/api/trackers', {
        headers: authHeaders,
    })

    if (!response.ok) {
        throw new Error(`Failed to fetch trackers: ${response.status}`)
    }

    const data = await response.json()
    return data.trackers || []
}

/**
 * Hook to fetch and cache issue trackers
 */
export function useTrackers() {
    return useQuery({
        queryKey: apiKeys.trackers(),
        queryFn: fetchTrackers,
        staleTime: 10 * 60 * 1000, // 10 minutes - trackers rarely change
        gcTime: 30 * 60 * 1000, // 30 minutes
        refetchOnWindowFocus: false,
    })
}

// ============================================================================
// ISSUE STATUSES
// ============================================================================

interface IssueStatus {
    id: number
    name: string
    is_closed: boolean
    is_default?: boolean
}

async function fetchIssueStatuses(): Promise<IssueStatus[]> {
    const authHeaders = getAuthHeaders()
    const response = await fetchWithAuthHandler('/api/issue_statuses', {
        headers: authHeaders,
    })

    if (!response.ok) {
        throw new Error(`Failed to fetch issue statuses: ${response.status}`)
    }

    const data = await response.json()
    return data.issue_statuses || []
}

/**
 * Hook to fetch and cache issue statuses
 */
export function useIssueStatuses() {
    return useQuery({
        queryKey: apiKeys.issueStatuses(),
        queryFn: fetchIssueStatuses,
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes
        refetchOnWindowFocus: false,
    })
}

// ============================================================================
// PRIORITIES
// ============================================================================

interface Priority {
    id: number
    name: string
    is_default?: boolean
}

async function fetchPriorities(): Promise<Priority[]> {
    const authHeaders = getAuthHeaders()
    const response = await fetchWithAuthHandler('/api/enumerations/issue_priorities', {
        headers: authHeaders,
    })

    if (!response.ok) {
        throw new Error(`Failed to fetch priorities: ${response.status}`)
    }

    const data = await response.json()
    return data.issue_priorities || []
}

/**
 * Hook to fetch and cache issue priorities
 */
export function usePriorities() {
    return useQuery({
        queryKey: apiKeys.priorities(),
        queryFn: fetchPriorities,
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes
        refetchOnWindowFocus: false,
    })
}

// ============================================================================
// TIME ENTRY ACTIVITIES
// ============================================================================

interface TimeActivity {
    id: number
    name: string
    is_default?: boolean
}

async function fetchTimeActivities(): Promise<TimeActivity[]> {
    const authHeaders = getAuthHeaders()
    const response = await fetchWithAuthHandler('/api/enumerations/time_entry_activities', {
        headers: authHeaders,
    })

    if (!response.ok) {
        throw new Error(`Failed to fetch time activities: ${response.status}`)
    }

    const data = await response.json()
    return data.time_entry_activities || []
}

/**
 * Hook to fetch and cache time entry activities
 */
export function useTimeActivities() {
    return useQuery({
        queryKey: apiKeys.timeActivities(),
        queryFn: fetchTimeActivities,
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes
        refetchOnWindowFocus: false,
    })
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook to prefetch all common filter options at once
 * Useful for pages that need multiple filter options
 */
export function usePrefetchFilterOptions() {
    const queryClient = useQueryClient()

    const prefetchAll = () => {
        queryClient.prefetchQuery({
            queryKey: apiKeys.trackers(),
            queryFn: fetchTrackers,
        })
        queryClient.prefetchQuery({
            queryKey: apiKeys.issueStatuses(),
            queryFn: fetchIssueStatuses,
        })
        queryClient.prefetchQuery({
            queryKey: apiKeys.priorities(),
            queryFn: fetchPriorities,
        })
    }

    return { prefetchAll }
}

/**
 * Hook to invalidate all cached API data
 * Useful for force refresh scenarios
 */
export function useInvalidateCommonApi() {
    const queryClient = useQueryClient()

    const invalidateAll = () => {
        queryClient.invalidateQueries({ queryKey: apiKeys.currentUser() })
        queryClient.invalidateQueries({ queryKey: apiKeys.projects() })
        queryClient.invalidateQueries({ queryKey: apiKeys.trackers() })
        queryClient.invalidateQueries({ queryKey: apiKeys.issueStatuses() })
        queryClient.invalidateQueries({ queryKey: apiKeys.priorities() })
        queryClient.invalidateQueries({ queryKey: apiKeys.timeActivities() })
    }

    return { invalidateAll }
}
